import { useQueryClient } from "@tanstack/react-query";
import React, { useContext, useState } from "react";
import { useMatch, useNavigate } from "react-router-dom";
import styled from "styled-components";

import {
  ClassroomSettingsModal,
  ClassroomSetupPrompt,
} from "@examspark/common-app";
import { Button, Tabs, Toolbar } from "@examspark/common-ui";
import { IconGearLine } from "@examspark/common-ui/dist/components/icons";
import { ParsedClassroom } from "@examspark/types";

import { WindowContext } from "../common/AppWrapper";
import Breadcrumbs from "../common/Breadcrumbs";
import FavoriteButton from "../common/FavoriteButton";
import LargeCalendarIcon from "../common/icons/LargeCalendarIcon";
import PageHead from "../common/PageHead";
import PageTitle from "../common/PageTitle";

import ClassroomLayout from "./ClassroomLayout";
import ClassroomOverview from "./ClassroomOverview";
import ClassroomPlanner from "./ClassroomPlanner";
import ClassroomResources from "./ClassroomResources";
import ClassroomSide from "./ClassroomSide";
import ClassroomUnits from "./ClassroomUnits";
import ClassroomPlannerCalendar from "./planners/ClassroomPlannerCalendar";

export const PaddedBox = styled.div`
  padding: 1rem;
  height: 10rem;
`;

const Classroom = (props: {
  classroom?: ParsedClassroom;
  onUpdate?: () => void;
}) => {
  const win = useContext(WindowContext);
  const navigate = useNavigate();
  const client = useQueryClient();
  const match = useMatch("/classroom/:id/:tab/*");
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  let classroomTitle = props.classroom?.name ?? "Loading...";
  if (props.classroom?.isArchived) {
    classroomTitle += " (Archived)";
  }

  return (
    <>
      {props.classroom && (
        <>
          <ClassroomSettingsModal
            size={win.device === "phone" ? "mobile" : "small"}
            classroom={props.classroom}
            isOpen={isSettingsModalOpen}
            onRequestClose={() => setIsSettingsModalOpen(false)}
            onReset={() => {
              client.invalidateQueries();
              navigate(`/classroom/${props.classroom?.id}`);
              setIsSettingsModalOpen(false);
            }}
            onDelete={() => {
              client.invalidateQueries();
              navigate("/");
              setIsSettingsModalOpen(false);
            }}
            onUpdate={() => {
              setIsSettingsModalOpen(false);
              props.onUpdate?.();
            }}
          />
        </>
      )}
      <ClassroomLayout
        main={
          <>
            <PageHead>
              <Breadcrumbs items={Breadcrumbs.Classroom()} />
              <PageTitle
                tools={
                  <Toolbar>
                    <FavoriteButton item={props.classroom} />
                    <Button
                      title="Configure"
                      icon={<IconGearLine />}
                      variant="outlined"
                      onClick={() => setIsSettingsModalOpen(true)}
                    >
                      Settings
                    </Button>
                  </Toolbar>
                }
                subtitle={props.classroom?.course?.name}
              >
                {classroomTitle}
              </PageTitle>
            </PageHead>
            {props.classroom !== undefined && !props.classroom?.course && (
              <ClassroomSetupPrompt
                data={props.classroom}
                onUpdate={props.onUpdate}
              />
            )}
            {props.classroom?.course && (
              <>
                <Tabs
                  value={match?.params.tab ?? "overview"}
                  onChange={(value) =>
                    navigate(value === "overview" ? "." : value)
                  }
                >
                  <Tabs.TabItem value="overview" title="Overview">
                    {win.device !== "phone" ? (
                      "Current Unit"
                    ) : (
                      <svg
                        id="Object"
                        height="16"
                        viewBox="0 0 32 32"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="m16 1a11 11 0 0 0 -11 11c0 5.81 9.8 18.1 10.22 18.63a1 1 0 0 0 1.56 0c.42-.53 10.22-12.82 10.22-18.63a11 11 0 0 0 -11-11zm0 14a4 4 0 1 1 4-4 4 4 0 0 1 -4 4z" />
                      </svg>
                    )}
                  </Tabs.TabItem>
                  <Tabs.TabItem value="units" title="Units">
                    {win.device !== "phone" ? (
                      "All Units / Modules"
                    ) : (
                      <svg
                        version="1.1"
                        id="Capa_1"
                        xmlns="http://www.w3.org/2000/svg"
                        x="0px"
                        y="0px"
                        viewBox="0 0 60.123 60.123"
                        height="16"
                      >
                        <g>
                          <path d="M57.124,51.893H16.92c-1.657,0-3-1.343-3-3s1.343-3,3-3h40.203c1.657,0,3,1.343,3,3S58.781,51.893,57.124,51.893z" />
                          <path
                            d="M57.124,33.062H16.92c-1.657,0-3-1.343-3-3s1.343-3,3-3h40.203c1.657,0,3,1.343,3,3
		C60.124,31.719,58.781,33.062,57.124,33.062z"
                          />
                          <path d="M57.124,14.231H16.92c-1.657,0-3-1.343-3-3s1.343-3,3-3h40.203c1.657,0,3,1.343,3,3S58.781,14.231,57.124,14.231z" />
                          <circle cx="4.029" cy="11.463" r="4.029" />
                          <circle cx="4.029" cy="30.062" r="4.029" />
                          <circle cx="4.029" cy="48.661" r="4.029" />
                        </g>
                      </svg>
                    )}
                  </Tabs.TabItem>
                  <Tabs.TabItem value="year" title="Calendar">
                    {win.device !== "phone" ? (
                      "Year at a Glance"
                    ) : (
                      <svg
                        id="Capa_1"
                        enableBackground="new 0 0 512 512"
                        height="16"
                        viewBox="0 0 512 512"
                      >
                        <LargeCalendarIcon />
                      </svg>
                    )}
                  </Tabs.TabItem>
                  <Tabs.TabItem value="resources" title="Resources">
                    {win.device !== "phone" ? (
                      "Resources"
                    ) : (
                      <svg
                        version="1.1"
                        id="Capa_1"
                        xmlns="http://www.w3.org/2000/svg"
                        x="0px"
                        y="0px"
                        viewBox="0 0 375 375"
                        height="16"
                      >
                        <g>
                          <g>
                            <path
                              d="M187.5,0C84.1,0,0,84.1,0,187.5S84.1,375,187.5,375S375,290.9,375,187.5S290.9,0,187.5,0z M187.5,337.5
			c-82.7,0-150-67.3-150-150s67.3-150,150-150s150,67.3,150,150S270.2,337.5,187.5,337.5z"
                            />
                          </g>
                        </g>
                        <g>
                          <g>
                            <path
                              d="M150,150L93.7,281.3L225,225l56.3-131.3L150,150z M187.5,206.3c-10.4,0-18.8-8.4-18.8-18.8c0-10.4,8.4-18.8,18.8-18.8
			s18.8,8.4,18.8,18.8C206.3,197.9,197.9,206.3,187.5,206.3z"
                            />
                          </g>
                        </g>
                      </svg>
                    )}
                  </Tabs.TabItem>
                </Tabs>
                {(match?.params.tab ?? "overview") === "overview" &&
                  props.classroom && (
                    <ClassroomOverview classroom={props.classroom} />
                  )}
                {match?.params.tab === "units" && props.classroom && (
                  <ClassroomUnits
                    classroom={props.classroom}
                    onUpdate={props.onUpdate}
                  />
                )}
                {match?.params.tab === "year" && props.classroom && (
                  <ClassroomPlanner classroom={props.classroom} />
                )}
                {match?.params.tab === "calendar" && props.classroom && (
                  <ClassroomPlannerCalendar classroom={props.classroom} />
                )}

                {match?.params.tab === "resources" && props.classroom && (
                  <ClassroomResources classroom={props.classroom} />
                )}
              </>
            )}
          </>
        }
        side={<ClassroomSide classroom={props.classroom} />}
      />
    </>
  );
};

export default Classroom;
